// ===========================================
// Admins tab: who is an Admin, and who is a support account (roles slice 2, R13)
// ===========================================
// A support account keeps every Admin power but is not the school office: office emails and
// requests go to the other Admins and the staff, and it is not the office in Messages. The
// server refuses marking the school's last regular Admin, and says so.

import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSchoolAdmins, useSetSupportAccount } from '../../../hooks/useCustomRoles';
import OwnerTitleCard from './OwnerTitleCard';

export default function AdminsTab() {
  const { data: admins, isLoading, isError } = useSchoolAdmins();
  const setSupport = useSetSupportAccount();

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load the Admins. Please try again.
      </Alert>
    );
  }

  return (
    <>
      <OwnerTitleCard />
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            A <strong>support account</strong> is an Admin who supports the school rather than running it. It keeps
            every Admin power, but office emails and requests (enquiries, extra-lesson requests, make-up bookings) go to
            the other Admins and the staff instead, and it is not the office in Messages. At least one Admin must stay a
            regular Admin.
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="center">Support account</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && (admins ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No Admins found.
                  </TableCell>
                </TableRow>
              )}
              {isLoading
                ? [...Array(2)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(3)].map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : (admins ?? []).map((admin) => {
                    const name = `${admin.firstName} ${admin.lastName}`;
                    const saving = setSupport.isPending && setSupport.variables?.userId === admin.id;
                    return (
                      <TableRow key={admin.id}>
                        <TableCell>
                          {name}
                          {!admin.isActive && <Chip label="Inactive" size="small" sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>{admin.email}</TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={admin.isSupportAccount}
                            disabled={setSupport.isPending}
                            onChange={(e) => setSupport.mutate({ userId: admin.id, value: e.target.checked })}
                            inputProps={{ 'aria-label': `Support account: ${name}` }}
                            sx={{ opacity: saving ? 0.5 : 1 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}
